'use client';

import React from 'react';
import { cn } from '@/utils';
import { 
  toggleVariants, 
  toggleSwitchVariants, 
  toggleThumbVariants, 
  toggleLabelVariants 
} from '@/components';

export interface ToggleProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /**
   * Whether the toggle is checked (on)
   */
  checked?: boolean;

  /**
   * Callback when toggle state changes
   */
  onChange?: (checked: boolean) => void;

  /**
   * Label text for the toggle
   */
  label?: string;

  /**
   * Whether the toggle is disabled
   */
  disabled?: boolean;

  /**
   * Position of the label relative to the toggle switch
   */
  labelPosition?: 'left' | 'right';

  /**
   * Unique identifier for the toggle input
   */
  id?: string;

  /**
   * Name attribute for the toggle input
   */
  name?: string;
}

/**
 * A toggle switch component for binary on/off states. Features a clean design
 * with optional labels that can be positioned on either side of the switch.
 */
export const Toggle: React.FC<ToggleProps> = ({
  checked = false,
  onChange,
  label,
  disabled = false,
  labelPosition = 'left',
  className,
  id,
  name,
  ...props
}) => {
  const handleToggle = () => {
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleToggle();
    }
  };

  const toggleSwitch = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      id={id}
      name={name}
      className={cn(toggleSwitchVariants({ checked, disabled }))}
    >
      <span 
        aria-hidden="true" 
        className={cn(toggleThumbVariants({ checked }))}
      />
    </button>
  );

  const labelElement = label ? (
    <label
      htmlFor={id}
      className={cn(toggleLabelVariants({ disabled }))}
    >
      {label}
    </label>
  ) : null;

  const containerClasses = cn(
    toggleVariants(),
    labelPosition === 'right' && 'flex-row-reverse',
    className
  );

  return (
    <div className={containerClasses} {...props}>
      {labelPosition === 'left' && labelElement}
      {toggleSwitch}
      {labelPosition === 'right' && labelElement}
    </div>
  );
};

Toggle.displayName = 'Toggle';

export default Toggle;